<script setup lang="ts">
import { localizePlace } from '@/data/regions'
import { computed, ref, watch, onUnmounted } from 'vue'
import { RouterLink } from 'vue-router'
import { Clock, Gavel, MapPin, Flame, Wheat, Car, Building2, Zap, Video, CheckCircle2 } from 'lucide-vue-next'
import { useFormatters } from '@/composables/useFormatters'
import { useI18n } from '@/composables/useI18n'
import type { Auction, Money } from '@/types'

interface Props {
  auction: Auction
  priceUpdate?: Money
}

const props = defineProps<Props>()
const { currency } = useFormatters()
const { formatMoney } = currency
const { t, currentLang } = useI18n()
const placeLabel = computed(() => localizePlace(props.auction.city, currentLang.value, props.auction.regionId))

const currentPrice = computed(() => props.priceUpdate || props.auction.currentPrice)

// Brief highlight when a live bid lands on this card
const justFlashed = ref(false)
let flashTimer: ReturnType<typeof setTimeout> | null = null
watch(() => props.auction.bidCount, () => {
  justFlashed.value = true
  if (flashTimer) clearTimeout(flashTimer)
  flashTimer = setTimeout(() => { justFlashed.value = false }, 1800)
})
onUnmounted(() => { if (flashTimer) clearTimeout(flashTimer) })

const timeLeft = computed(() => {
  if (!props.auction.endsAt) return { text: '', ended: false, urgent: false }
  const diff = new Date(props.auction.endsAt).getTime() - Date.now()
  if (diff <= 0) return { text: t('auction.auctionEnded'), ended: true, urgent: false }
  const hours = Math.floor(diff / 3600000)
  const minutes = Math.floor((diff % 3600000) / 60000)
  const days = Math.floor(hours / 24)
  if (days > 0) return { text: `${days}${t('landing.unitDay')} ${hours % 24}${t('landing.unitHour')}`, ended: false, urgent: false }
  if (hours > 0) return { text: `${hours}${t('landing.unitHour')} ${minutes}${t('landing.unitMin')}`, ended: false, urgent: hours < 2 }
  return { text: `${minutes}${t('landing.unitMin')}`, ended: false, urgent: true }
})

// A lot is "over" either because the clock ran out or because the backend
// already moved it into one of the ended_* states.
const isEnded = computed(() => timeLeft.value.ended || String(props.auction.status || '').startsWith('ended'))

// Never label a finished lot's price as the "current bid" — it is the final one.
const priceLabel = computed(() => {
  if (!isEnded.value) return t('ui.auctionCard.s2')
  return t('auctionCard.finalPrice')
    || (props.auction.status === 'ended_sold' ? t('status.auction.endedSold') : t('time.ended'))
})

const spec = computed(() => {
  const a = props.auction
  if (a.livestock) {
    const parts = [
      a.livestock.weightKg ? `${a.livestock.weightKg} ${t('lotAttributes.weightKg')}`.trim() : '',
      a.livestock.breed || '',
    ].filter(Boolean)
    return parts.length ? { icon: Wheat, text: parts.join(' · ') } : null
  }
  if (a.vehicle) {
    const parts = [
      a.vehicle.year,
      a.vehicle.steering === 'right' ? t('auctionCard.rightHandDrive') : t('auctionCard.leftHandDrive'),
      a.vehicle.isCustomsCleared ? t('auctionCard.customsCleared') : '',
    ].filter(Boolean)
    return parts.length ? { icon: Car, text: parts.join(' · ') } : null
  }
  if (a.realEstate) {
    const parts = [
      a.realEstate.areaSqm ? `${a.realEstate.areaSqm} ${t('lotAttributes.sqmUnit')}`.trim() : '',
      a.realEstate.deedType === 'red_book' ? t('auctionCard.redBook') : '',
    ].filter(Boolean)
    return parts.length ? { icon: Building2, text: parts.join(' · ') } : null
  }
  return null
})
</script>

<template>
  <RouterLink
    :to="`/auctions/${auction.id}`"
    class="group relative flex h-full flex-col overflow-hidden rounded-card border border-border bg-surface transition-all duration-300 hover:-translate-y-0.5 hover:border-ink/15 hover:shadow-[0_16px_36px_-16px_rgba(20,23,31,0.22)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
    :class="{ 'ring-2 ring-primary ring-offset-2': justFlashed }"
  >
    <div class="relative aspect-[4/3] overflow-hidden bg-accent">
      <img
        :src="auction.images[0] || '/placeholder-lot.svg'"
        :alt="auction.title"
        class="h-full w-full object-cover transition-all duration-700 ease-out group-hover:scale-[1.04]"
        :class="isEnded ? 'grayscale opacity-60' : ''"
        loading="lazy"
      />

      <div class="pointer-events-none absolute left-2.5 right-2.5 top-2.5 flex items-start justify-between gap-2">
        <span
          class="inline-flex items-center gap-1 rounded-full px-2 py-1 text-[11px] font-semibold tabular backdrop-blur"
          :class="isEnded
            ? 'bg-ink/70 text-white/80'
            : timeLeft.urgent
              ? 'bg-surface/95 text-live shadow-sm'
              : 'bg-surface/90 text-text-primary shadow-sm'"
        >
          <span v-if="timeLeft.urgent && !isEnded" class="relative inline-flex h-1.5 w-1.5 rounded-full bg-current live-dot" />
          <Clock v-else class="h-3 w-3" />
          <span>{{ isEnded ? t('auction.auctionEnded') : timeLeft.text }}</span>
        </span>

        <span class="flex items-center gap-1">
          <span v-if="auction.videoUrl" class="inline-flex items-center rounded-full bg-ink/80 p-1.5 text-white" :title="t('ui.auctionCard.s4')">
            <Video class="h-3 w-3" />
          </span>
          <span v-if="auction.isBlitz && !isEnded" class="inline-flex items-center gap-1 rounded-full bg-primary px-2 py-1 text-[11px] font-bold uppercase leading-none text-onPrimary shadow-sm">
            <Flame class="h-3 w-3" />{{ t('auctionCard.flashBadge') }}
          </span>
        </span>
      </div>

      <div v-if="spec" class="pointer-events-none absolute bottom-2.5 left-2.5 right-2.5">
        <span class="inline-flex max-w-full items-center gap-1.5 rounded-lg bg-ink/75 px-2 py-1 text-[11px] font-medium text-white backdrop-blur">
          <component :is="spec.icon" class="h-3 w-3 shrink-0 text-primary" />
          <span class="truncate">{{ spec.text }}</span>
        </span>
      </div>
    </div>

    <div class="flex flex-1 flex-col gap-2 p-3.5">
      <div class="flex items-center justify-between gap-2 text-[11px] text-text-muted">
        <span class="inline-flex min-w-0 flex-1 items-center gap-1">
          <MapPin class="h-3 w-3 shrink-0" />
          <span class="truncate">{{ placeLabel }}</span>
        </span>
        <!-- The city keeps the room: the badge only earns its label once the card
             is wide enough (xl grids), and stays icon-only — but still named for
             assistive tech — below that. -->
        <span
          v-if="auction.seller?.kycStatus === 'verified'"
          class="inline-flex shrink-0 items-center gap-1 text-success"
          :title="t('landing.verifiedSeller')"
        >
          <CheckCircle2 class="h-3 w-3" />
          <span class="sr-only xl:not-sr-only xl:inline">{{ t('landing.verifiedSeller') }}</span>
        </span>
      </div>

      <h3 class="font-display text-[15px] font-semibold leading-snug text-text-primary line-clamp-2 transition-colors group-hover:text-ink">
        {{ auction.title }}
      </h3>

      <!-- Stacked rather than side-by-side: the eyebrow shares its line only with
           the compact bid chip, and the price then owns a full-width line of its
           own, so the number never splits from its currency and the label never
           needs an ellipsis on narrow (≈220px) cards. -->
      <div class="mt-auto flex flex-col gap-1 border-t border-border pt-2.5">
        <div class="flex flex-wrap items-center gap-x-2 gap-y-1">
          <span v-if="priceLabel" class="text-[11px] font-semibold uppercase tracking-[0.14em] text-text-muted">{{ priceLabel }}</span>
          <span class="ml-auto inline-flex shrink-0 items-center gap-1 rounded-full bg-accent px-2 py-0.5 text-[11px] font-semibold tabular text-text-secondary" :class="{ 'animate-bounce': justFlashed }">
            <Gavel class="h-3 w-3" />{{ auction.bidCount }}
          </span>
        </div>
        <div class="whitespace-nowrap font-display text-[15px] sm:text-base font-bold tabular leading-tight transition-colors" :class="[isEnded ? 'text-text-secondary' : 'text-text-primary', { 'text-primary-hover': justFlashed }]">
          {{ formatMoney(currentPrice) }}
        </div>
        <span
          v-if="auction.buyNowPrice && !isEnded"
          class="inline-flex items-center gap-1 self-end whitespace-nowrap text-[11px] tabular text-text-muted"
          :title="t('auctionCard.buyNow')"
        >
          <Zap class="h-3 w-3 shrink-0 text-warning" />{{ formatMoney(auction.buyNowPrice) }}
        </span>
      </div>
    </div>
  </RouterLink>
</template>
